import { LANG_OPTIONS, SORT_OPTIONS, YEAR_OPTIONS } from '@/lib/constants';
import type { SortLang, Taxonomy } from '@/types/anime';

export interface BrowseFilterValues {
	category: string;
	country: string;
	year: string;
	/** Dạng "field-type", ví dụ "_id-desc" — khớp với query param `sort`. */
	sort: string;
	lang: SortLang;
}

interface FilterBarProps {
	values: BrowseFilterValues;
	genres: Taxonomy[];
	countries: Taxonomy[];
	onChange: (patch: Partial<BrowseFilterValues>) => void;
	onReset: () => void;
}

const selectClass =
	'min-w-0 rounded-[10px] border border-line bg-[#15182a] px-3 py-2 text-sm text-ink outline-0 transition focus:border-iris';

export function FilterBar({
	values,
	genres,
	countries,
	onChange,
	onReset,
}: FilterBarProps) {
	const hasFilter =
		Boolean(values.category || values.country || values.year || values.lang) ||
		values.sort !== 'modified.time-desc';

	return (
		<div className="mb-5 grid gap-2.5 rounded-panel border border-line bg-surface/70 p-3.5 min-[700px]:grid-cols-2 min-[1100px]:grid-cols-5">
			<label className="grid gap-1">
				<span className="text-[11px] font-bold tracking-wide text-faint uppercase">
					Thể loại
				</span>
				<select
					className={selectClass}
					value={values.category}
					onChange={(event) => onChange({ category: event.target.value })}
				>
					<option value="">Tất cả thể loại</option>
					{genres.map((genre) => (
						<option key={genre.slug} value={genre.slug}>
							{genre.name}
						</option>
					))}
				</select>
			</label>

			<label className="grid gap-1">
				<span className="text-[11px] font-bold tracking-wide text-faint uppercase">
					Quốc gia
				</span>
				<select
					className={selectClass}
					value={values.country}
					onChange={(event) => onChange({ country: event.target.value })}
				>
					<option value="">Tất cả quốc gia</option>
					{countries.map((country) => (
						<option key={country.slug} value={country.slug}>
							{country.name}
						</option>
					))}
				</select>
			</label>

			<label className="grid gap-1">
				<span className="text-[11px] font-bold tracking-wide text-faint uppercase">
					Năm
				</span>
				<select
					className={selectClass}
					value={values.year}
					onChange={(event) => onChange({ year: event.target.value })}
				>
					<option value="">Mọi năm</option>
					{YEAR_OPTIONS.map((year) => (
						<option key={year} value={year}>
							{year}
						</option>
					))}
				</select>
			</label>

			<label className="grid gap-1">
				<span className="text-[11px] font-bold tracking-wide text-faint uppercase">
					Bản phát
				</span>
				<select
					className={selectClass}
					value={values.lang}
					onChange={(event) =>
						onChange({ lang: event.target.value as SortLang })
					}
				>
					{LANG_OPTIONS.map((option) => (
						<option key={option.value || 'all'} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</label>

			<label className="grid gap-1">
				<span className="text-[11px] font-bold tracking-wide text-faint uppercase">
					Sắp xếp
				</span>
				<div className="flex gap-2">
					<select
						className={`${selectClass} flex-1`}
						value={values.sort}
						onChange={(event) => onChange({ sort: event.target.value })}
					>
						{SORT_OPTIONS.map((option) => (
							<option
								key={`${option.sortField}-${option.sortType}`}
								value={`${option.sortField}-${option.sortType}`}
							>
								{option.label}
							</option>
						))}
					</select>
					{hasFilter && (
						<button
							type="button"
							onClick={onReset}
							title="Xoá bộ lọc"
							className="shrink-0 rounded-[10px] border border-line px-3 text-sm text-muted transition hover:border-accent hover:text-ink"
						>
							Xoá
						</button>
					)}
				</div>
			</label>
		</div>
	);
}
